/**
 * Whether a booking has been promised to a customer, and therefore whether the
 * board will let it be dragged.
 *
 * The dispatch board holds two kinds of booking and the difference is the whole
 * point of it. An unconfirmed slot is a plan: the office thinks Tuesday at nine
 * works, nobody has said so out loud, and it may not go ahead at all. Once
 * somebody has agreed to that time it stops being a plan and becomes a promise,
 * and a promise that quietly slides half an hour because a pointer was in the
 * wrong place is a van arriving when nobody is home.
 *
 * So `PROPOSED` is fluid and everything from `CONFIRMED` onwards is locked. The
 * ladder matters: a technician who is `EN_ROUTE` or `ON_SITE` was necessarily
 * confirmed first, and a booking that unlocked itself the moment the van pulled
 * out would be the one moment it must not move. `COMPLETED` was already refused
 * server-side before any of this existed.
 *
 * Cancelled and no-show are fluid because there is no longer a promise to keep.
 *
 * Declared once here because three places have to agree: the board decides
 * whether to arm a drag, the quick menu decides whether to offer an unlock, and
 * the API refuses the write regardless of what any client believes. Decision 76.
 */

/** The statuses a booking can be dragged in. Everything else is a promise. */
const FLUID = ['PROPOSED', 'CANCELLED', 'NO_SHOW'] as const

/**
 * Whether the board should refuse to move this booking.
 *
 * Takes the status as a string rather than the Prisma enum so the web client
 * can call it without importing `@fieldops/db`, which is the same reason
 * `job-worklist.ts` is shaped the way it is.
 */
export function visitLocked(status: string): boolean {
  return !(FLUID as readonly string[]).includes(status)
}

/**
 * Whether unlocking is a thing that can be done to this booking.
 *
 * Only a plain confirmation unlocks. Sending `EN_ROUTE` back to `PROPOSED`
 * would throw away where the technician actually is to win an argument with a
 * drag, so those say why they will not move instead of offering a way.
 */
export function visitUnlockable(status: string): boolean {
  return status === 'CONFIRMED'
}

/** Why the board would not move it, in the words the toast uses. */
export function visitLockReason(status: string): string | null {
  if (!visitLocked(status)) return null
  if (status === 'CONFIRMED') {
    return 'That booking is confirmed with the customer. Unlock it to move it.'
  }
  // The wording the API has always used, kept so the one thing that was
  // already refused says what it always said.
  if (status === 'COMPLETED') return 'That visit is already completed. Reopen it before rescheduling.'
  return 'That technician is already on their way. Change the time from the booking if it really has moved.'
}
